import React, { useState, useEffect } from 'react';
import { db, storage, functions } from '../lib/firebase';
import { doc, updateDoc, addDoc, deleteDoc, collection, getDocs, query, where, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { 
  FlagIcon,
  CalendarIcon,
  PhotoIcon,
  TrashIcon,
  PlusIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline';

// Milestone types will be loaded from settings

export default function MilestoneManager({ projectId, milestones = [], onClose, onUpdate }) {
  const [editingMilestones, setEditingMilestones] = useState([...milestones]);
  const [milestoneTypes, setMilestoneTypes] = useState([]);
  const [newMilestone, setNewMilestone] = useState({
    type: 'custom',
    title: '',
    description: '',
    date: new Date().toISOString().split('T')[0], // Set default date to today
    visibleToCustomer: true,
    completed: false,
    images: []
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingMilestoneId, setEditingMilestoneId] = useState(null);

  useEffect(() => {
    loadMilestoneTypes();
  }, []);

  const loadMilestoneTypes = async () => {
    try {
      const settingsDoc = await getDocs(query(collection(db, 'settings'), where('type', '==', 'milestoneTypes')));
      if (!settingsDoc.empty) {
        const data = settingsDoc.docs[0].data();
        setMilestoneTypes(data.types || []);
      } else {
        // Set default milestone types if none exist
        const defaultTypes = [
          { value: 'project-start', label: 'Project Start', icon: '🚀', color: '#10b981' },
          { value: 'design-approval', label: 'Design Approval', icon: '✅', color: '#3b82f6' },
          { value: 'ordering', label: 'Ordering', icon: '📦', color: '#8b5cf6' },
          { value: 'delivery', label: 'Delivery', icon: '🚚', color: '#eab308' },
          { value: 'installation-start', label: 'Installation Start', icon: '🔧', color: '#f97316' },
          { value: 'painting', label: 'Painting', icon: '🎨', color: '#ec4899' },
          { value: 'styling', label: 'Styling', icon: '✨', color: '#6366f1' },
          { value: 'photography', label: 'Photography', icon: '📸', color: '#14b8a6' },
          { value: 'completion', label: 'Project Completion', icon: '⭐', color: '#10b981' },
          { value: 'custom', label: 'Custom Milestone', icon: '🚩', color: '#6b7280' }
        ];
        setMilestoneTypes(defaultTypes);
      }
    } catch (error) {
      console.error('Error loading milestone types:', error);
    }
  };

  const handleAddMilestone = () => {
    if (!newMilestone.title || !newMilestone.date) {
      alert('Please provide a title and date for the milestone');
      return;
    }

    // Parse the date string properly to avoid timezone issues
    const [year, month, day] = newMilestone.date.split('-').map(Number);
    const milestoneDate = new Date(year, month - 1, day); // month is 0-indexed
    
    const milestone = {
      ...newMilestone,
      id: `temp-${Date.now()}`,
      date: milestoneDate,
      createdAt: new Date()
    };

    setEditingMilestones([...editingMilestones, milestone]);
    setNewMilestone({
      type: 'custom',
      title: '',
      description: '',
      date: new Date().toISOString().split('T')[0], // Reset to today's date
      visibleToCustomer: true,
      completed: false,
      images: []
    });
  };

  const handleRemoveMilestone = (milestoneId) => {
    setEditingMilestones(editingMilestones.filter(m => m.id !== milestoneId));
  };

  const handleToggleVisibility = (milestoneId) => {
    setEditingMilestones(editingMilestones.map(m => 
      m.id === milestoneId ? { ...m, visibleToCustomer: !m.visibleToCustomer } : m
    ));
  };

  const handleToggleCompleted = (milestoneId) => {
    setEditingMilestones(editingMilestones.map(m => 
      m.id === milestoneId ? { ...m, completed: !m.completed } : m
    ));
  };

  const handleEditMilestone = (milestone) => {
    setEditingMilestoneId(milestone.id);
  };

  const handleUpdateMilestone = (milestoneId, field, value) => {
    setEditingMilestones(editingMilestones.map(m => 
      m.id === milestoneId ? { ...m, [field]: value } : m
    ));
  };

  const handleSaveEdit = (milestoneId) => {
    setEditingMilestoneId(null);
  };

  const handleCancelEdit = (milestoneId) => {
    setEditingMilestoneId(null);
    // Reset milestone data from original milestones array if needed
  };

  const handleImageUpload = async (milestoneId, files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    const uploadedImages = [];

    try {
      for (const file of files) {
        const timestamp = Date.now();
        const fileName = `projects/${projectId}/milestones/${milestoneId}/${timestamp}_${file.name}`;
        const storageRef = ref(storage, fileName);
        
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        
        uploadedImages.push({
          url,
          fileName: file.name,
          uploadedAt: new Date(),
          caption: ''
        });
      }

      setEditingMilestones(editingMilestones.map(m => 
        m.id === milestoneId 
          ? { ...m, images: [...(m.images || []), ...uploadedImages] }
          : m
      ));
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (milestoneId, imageIndex) => {
    setEditingMilestones(editingMilestones.map(m => 
      m.id === milestoneId 
        ? { ...m, images: m.images.filter((_, i) => i !== imageIndex) }
        : m
    ));
  };

  const handleUpdateImageCaption = (milestoneId, imageIndex, caption) => {
    setEditingMilestones(editingMilestones.map(m => 
      m.id === milestoneId 
        ? { 
            ...m, 
            images: m.images.map((img, i) => 
              i === imageIndex ? { ...img, caption } : img
            )
          }
        : m
    ));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Identify new milestones that are visible to customers
      const originalMilestoneIds = milestones.map(m => m.id);
      const newCustomerVisibleMilestones = editingMilestones.filter(m => 
        m.id.startsWith('temp-') && m.visibleToCustomer !== false
      );

      // Update project document with milestones
      const updatedMilestones = editingMilestones.map(m => ({
        ...m,
        id: m.id.startsWith('temp-') ? `milestone-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` : m.id
      }));

      await updateDoc(doc(db, 'projects', projectId), {
        milestones: updatedMilestones,
        milestonesUpdatedAt: new Date()
      });

      // Send email notifications for new customer-visible milestones
      if (newCustomerVisibleMilestones.length > 0) {
        console.log(`Found ${newCustomerVisibleMilestones.length} new customer-visible milestones to notify about`);
        
        try {
          // Get project details for customer info
          const projectDoc = await getDoc(doc(db, 'projects', projectId));
          const projectData = projectDoc.data();
          
          // Check for customer email in different possible locations
          const customerEmail = projectData?.customerEmail || projectData?.customer?.email;
          const customerName = projectData?.customerName || projectData?.clientName || projectData?.customer?.name;
          
          console.log('Project data for notifications:', {
            hasCustomerEmail: !!customerEmail,
            customerEmail: customerEmail,
            customerName: customerName,
            projectName: projectData?.name,
            projectStructure: {
              hasCustomerEmailField: !!projectData?.customerEmail,
              hasCustomerObject: !!projectData?.customer,
              customerObjectEmail: projectData?.customer?.email
            }
          });
          
          if (customerEmail) {
            const sendMilestoneNotification = httpsCallable(functions, 'sendMilestoneNotification');
            
            // Send notification for each new milestone
            for (const milestone of newCustomerVisibleMilestones) {
              // Find the updated milestone with the new ID
              const updatedMilestone = updatedMilestones.find(m => 
                m.title === milestone.title && m.date === milestone.date
              );
              
              if (updatedMilestone) {
                console.log(`Sending email notification for milestone: ${milestone.title}`, {
                  projectId,
                  milestoneId: updatedMilestone.id,
                  customerEmail: customerEmail
                });
                
                try {
                  const result = await sendMilestoneNotification({
                    projectId: projectId,
                    milestoneId: updatedMilestone.id,
                    customerEmail: customerEmail,
                    customerName: customerName
                  });
                  
                  console.log(`Email notification result for milestone ${milestone.title}:`, result);
                } catch (emailError) {
                  console.error(`Failed to send email for milestone ${milestone.title}:`, emailError);
                  console.error('Email error details:', {
                    error: emailError,
                    code: emailError.code,
                    message: emailError.message,
                    details: emailError.details
                  });
                  alert(`Warning: Email notification failed for "${milestone.title}". Error: ${emailError.message || emailError}. Milestone saved successfully, but customer was not notified.`);
                }
              } else {
                console.warn(`Could not find updated milestone for ${milestone.title}`);
              }
            }
          } else {
            console.warn('No customer email found in project data - cannot send notifications');
            alert(`Warning: No customer email found for this project. Milestones saved successfully, but customer notifications could not be sent.`);
          }
        } catch (error) {
          console.error('Error sending milestone notifications:', error);
          alert(`Warning: Failed to send email notifications. Milestones saved successfully, but customer was not notified.`);
        }
      } else {
        console.log('No new customer-visible milestones to notify about');
      }
      
      onUpdate && onUpdate(updatedMilestones);
      onClose();
    } catch (error) {
      console.error('Error saving milestones:', error);
      alert('Failed to save milestones');
    } finally {
      setSaving(false);
    }
  };

  const formatDateForInput = (date) => {
    if (!date) return '';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center">
            <FlagIcon className="w-6 h-6 text-purple-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">Manage Milestones</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Add New Milestone */}
          <div className="bg-purple-50 rounded-xl p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Milestone</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={newMilestone.type}
                  onChange={(e) => {
                    const type = e.target.value;
                    const typeInfo = milestoneTypes.find(t => t.value === type);
                    const newTitle = type !== 'custom' ? typeInfo?.label || '' : newMilestone.title;
                    setNewMilestone({ 
                      ...newMilestone, 
                      type,
                      title: newTitle,
                      // Auto-fill description with title when type changes
                      description: type !== 'custom' ? newTitle : newMilestone.description
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  {milestoneTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.icon} {type.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={newMilestone.title}
                  onChange={(e) => setNewMilestone({ 
                    ...newMilestone, 
                    title: e.target.value,
                    // Auto-fill description with title if description is empty
                    description: newMilestone.description === '' ? e.target.value : newMilestone.description
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Milestone title"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={newMilestone.date}
                  onChange={(e) => setNewMilestone({ ...newMilestone, date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={newMilestone.description}
                  onChange={(e) => setNewMilestone({ ...newMilestone, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Optional description"
                />
              </div>
            </div>
            
            <div className="mt-4 flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={newMilestone.visibleToCustomer}
                  onChange={(e) => setNewMilestone({ ...newMilestone, visibleToCustomer: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Visible to Customer</span>
              </label>
              
              <button
                onClick={handleAddMilestone}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add Milestone
              </button>
            </div>
          </div>

          {/* Existing Milestones */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Project Milestones</h3>
            
            {editingMilestones.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No milestones added yet. Add your first milestone above.
              </div>
            ) : (
              <div className="space-y-4">
                {editingMilestones.sort((a, b) => {
                  const dateA = a.date.toDate ? a.date.toDate() : new Date(a.date);
                  const dateB = b.date.toDate ? b.date.toDate() : new Date(b.date);
                  return dateA - dateB;
                }).map((milestone) => {
                  const typeInfo = milestoneTypes.find(t => t.value === milestone.type) || milestoneTypes.find(t => t.value === 'custom') || { icon: '🚩', label: 'Unknown' };
                  
                  const isEditing = editingMilestoneId === milestone.id;
                  
                  return (
                    <div key={milestone.id} className="bg-white border border-gray-200 rounded-xl p-4">
                      {isEditing ? (
                        /* Edit Form */
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                              <select
                                value={milestone.type}
                                onChange={(e) => {
                                  const newType = e.target.value;
                                  const typeInfo = milestoneTypes.find(t => t.value === newType);
                                  const newTitle = newType !== 'custom' ? typeInfo?.label || '' : milestone.title;
                                  handleUpdateMilestone(milestone.id, 'type', newType);
                                  if (newType !== 'custom') {
                                    handleUpdateMilestone(milestone.id, 'title', newTitle);
                                    if (milestone.description === milestone.title) {
                                      handleUpdateMilestone(milestone.id, 'description', newTitle);
                                    }
                                  }
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              >
                                {milestoneTypes.map(type => (
                                  <option key={type.value} value={type.value}>
                                    {type.icon} {type.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                              <input
                                type="text"
                                value={milestone.title}
                                onChange={(e) => {
                                  handleUpdateMilestone(milestone.id, 'title', e.target.value);
                                  if (milestone.description === milestone.title) {
                                    handleUpdateMilestone(milestone.id, 'description', e.target.value);
                                  }
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                              <input
                                type="date"
                                value={formatDateForInput(milestone.date)}
                                onChange={(e) => {
                                  const [year, month, day] = e.target.value.split('-').map(Number);
                                  const newDate = new Date(year, month - 1, day);
                                  handleUpdateMilestone(milestone.id, 'date', newDate);
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                              <input
                                type="text"
                                value={milestone.description}
                                onChange={(e) => handleUpdateMilestone(milestone.id, 'description', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              />
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={milestone.visibleToCustomer}
                                onChange={(e) => handleUpdateMilestone(milestone.id, 'visibleToCustomer', e.target.checked)}
                                className="mr-2"
                              />
                              <span className="text-sm text-gray-700">Visible to Customer</span>
                            </label>
                            
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleCancelEdit(milestone.id)}
                                className="px-3 py-1 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => handleSaveEdit(milestone.id)}
                                className="px-3 py-1 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        /* Display View */
                        <div className="space-y-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center mb-2">
                                <span className="text-2xl mr-3">{typeInfo.icon}</span>
                                <div>
                                  <h4 className="font-semibold text-gray-900">{milestone.title}</h4>
                                  <p className="text-sm text-gray-600">
                                    {formatDateForInput(milestone.date)}
                                    {milestone.description && ` • ${milestone.description}`}
                                  </p>
                                </div>
                              </div>
                            
                            {/* Images */}
                            {milestone.images && milestone.images.length > 0 && (
                              <div className="mt-3 space-y-2">
                                <div className="text-sm font-medium text-gray-700">Photos:</div>
                                <div className="grid grid-cols-3 gap-2">
                                  {milestone.images.map((image, index) => (
                                    <div key={index} className="relative group">
                                      <img
                                        src={image.url}
                                        alt={`Milestone photo ${index + 1}`}
                                        className="w-full h-24 object-cover rounded-lg"
                                      />
                                      <input
                                        type="text"
                                        value={image.caption || ''}
                                        onChange={(e) => handleUpdateImageCaption(milestone.id, index, e.target.value)}
                                        placeholder="Caption"
                                        className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded-b-lg"
                                      />
                                      <button
                                        onClick={() => handleRemoveImage(milestone.id, index)}
                                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                      >
                                        <XMarkIcon className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Add Images */}
                            <div className="mt-3">
                              <label className="cursor-pointer inline-flex items-center px-3 py-1 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm">
                                <PhotoIcon className="w-4 h-4 mr-2" />
                                Add Photos
                                <input
                                  type="file"
                                  multiple
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => handleImageUpload(milestone.id, Array.from(e.target.files))}
                                  disabled={uploading}
                                />
                              </label>
                            </div>
                          </div> {/* <-- closes .flex-1 */}
                          </div> {/* <-- closes .flex items-start justify-between */}
                          
                          {/* Actions */}
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleToggleCompleted(milestone.id)}
                              className={`p-2 rounded-lg transition-colors ${
                                milestone.completed 
                                  ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                                  : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                              }`}
                              title={milestone.completed ? 'Mark as incomplete' : 'Mark as complete'}
                            >
                              <CheckCircleIcon className="w-5 h-5" />
                            </button>
                            
                            <button
                              onClick={() => handleToggleVisibility(milestone.id)}
                              className={`p-2 rounded-lg transition-colors ${
                                milestone.visibleToCustomer 
                                  ? 'bg-blue-100 text-blue-600 hover:bg-blue-200' 
                                  : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                              }`}
                              title={milestone.visibleToCustomer ? 'Hide from customer' : 'Show to customer'}
                            >
                              {milestone.visibleToCustomer ? (
                                <EyeIcon className="w-5 h-5" />
                              ) : (
                                <EyeSlashIcon className="w-5 h-5" />
                              )}
                            </button>
                            
                            <button
                              onClick={() => handleEditMilestone(milestone)}
                              className="p-2 bg-yellow-100 text-yellow-600 rounded-lg hover:bg-yellow-200 transition-colors"
                              title="Edit milestone"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            
                            <button
                              onClick={() => handleRemoveMilestone(milestone.id)}
                              className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                              title="Remove milestone"
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              'Save Milestones'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
